import { formatDate, formatEventTime } from '@simpleacs/shared';
export function formatRelative(ts: number | null | undefined): string {
  if (!ts) return 'never';
  const diff = Date.now() - ts;
  if (diff < 0) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 45) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return formatDate(ts);
}

/**
 * Every time the web prints is on the platform's clock, EAT, whatever zone
 * the viewer's computer is set to (operator, 2026-10-05: "set the timezone
 * to nairobi across the platform, everywhere, those timestamps are wrong").
 * The formatters live in @simpleacs/shared so the page, the API's sentences
 * and the mobile app print the same wall clock for the same instant.
 */
export {
  formatDate,
  formatDateTime,
  formatDayMonth,
  formatEventTime,
  formatInstant,
  formatTime,
  platformIsoDate,
  platformWallClock,
  PLATFORM_TIME_ZONE,
  PLATFORM_TIME_ZONE_ABBREVIATION,
} from '@simpleacs/shared';

/**
 * Re-exported, not reimplemented: the API states byte counts in its own notes
 * ("3.9 GB free of 40 GB"), and two copies of this rounding rule would let the
 * page and the server disagree about the same number.
 */
export { formatBytes } from '@simpleacs/shared';

/**
 * Money, likewise re-exported rather than reimplemented — and here it matters
 * more than for bytes. The API renders the same invoice into a PDF, and a page
 * that rounds or groups an amount differently from its own printed copy is the
 * one discrepancy an invoice cannot survive.
 */
export { currencyExponent, currencySymbol, formatMoney } from '@simpleacs/shared';

export function formatUptime(seconds: number | null | undefined): string {
  if (!seconds || seconds < 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/**
 * "How long ago", at `formatUptime`'s granularity, for a stamp shown beside
 * a duration. `formatRelative` floors to the largest unit, which is right for
 * a list column; next to "up 2h 49m" it printed "went down 2h ago" for an ONU
 * that lost power 2h 51m earlier and came back two minutes later (Elizabeth
 * Kariuki - 767_1, 2026-09-21) — two readings of one clock that appeared to
 * contradict each other.
 */
export function formatAgo(ts: number | null | undefined): string {
  if (!ts) return 'never';
  const seconds = (Date.now() - ts) / 1000;
  if (seconds < 60) return 'just now';
  return `${formatUptime(seconds)} ago`;
}

export function truncate(value: string, max = 32): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

/** Latency in the unit a human reads at a glance: ms under a second, then seconds, then minutes. */
export function formatLatency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

/**
 * Wall-clock stamp for event rows: `15:23:47 - 3/June/2026`.
 *
 * "3 minutes ago" answers how recent; this answers exactly when, which is what
 * an operator correlates against a power cut or a fibre cut ticket. Seconds are
 * kept because ONU drops on one PON port land within the same minute.
 */
export function formatTimestamp(ts: number | null | undefined): string {
  if (!ts) return '—';
  return formatEventTime(ts);
}
